import { App } from "antd";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { getFriendlyErrorMessage, extractErrorInfo } from "@/utils/api";

/**
 * Custom hook for consistent error handling across the application
 * Provides standardized error messages and handles different error types
 * Automatically redirects to login for 401 Unauthenticated errors
 */
export function useErrorHandler() {
  const { message } = App.useApp();
  const navigate = useNavigate();
  const { logout } = useAuth();

  /**
   * Check if error is an authentication error (401 Unauthenticated)
   */
  const isAuthenticationError = (error: any): boolean => {
    const errorInfo = extractErrorInfo(error);
    const statusCode = error?.response?.status;

    // Check for 401 status code
    if (statusCode === 401) {
      console.log(
        "🔍 useErrorHandler: 401 status code detected - authentication error"
      );
      return true;
    }

    // Check for Unauthenticated error type
    if (errorInfo.error === "Unauthenticated") {
      console.log(
        "🔍 useErrorHandler: Unauthenticated error type detected - authentication error"
      );
      return true;
    }

    // Check for specific authentication error codes
    const authCodes = [
      "INVALID_CREDENTIALS",
      "NO_ACTIVE_SESSION",
      "INVALID_SESSION",
      "UNAUTHENTICATED",
    ];

    const isAuthCode = authCodes.includes(errorInfo.code || "");
    if (isAuthCode) {
      console.log(
        "🔍 useErrorHandler: Authentication error code detected:",
        errorInfo.code
      );
    }

    return isAuthCode;
  };

  /**
   * Check if error is an authorization error (403 Forbidden)
   */
  const isAuthorizationError = (error: any): boolean => {
    const errorInfo = extractErrorInfo(error);
    const statusCode = error?.response?.status;

    // Check for 403 status code
    if (statusCode === 403) {
      return true;
    }

    // Check for Forbidden error type
    if (errorInfo.error === "Forbidden") {
      return true;
    }

    // Check for specific authorization error codes
    const authCodes = [
      "INSUFFICIENT_PERMISSIONS",
      "FORBIDDEN",
      "ACCESS_DENIED",
    ];

    return authCodes.includes(errorInfo.code || "");
  };

  /**
   * Handle authentication errors by logging out and redirecting to login
   */
  const handleAuthenticationError = (error: any) => {
    console.log("Authentication error detected, redirecting to login:", error);

    // Logout the user
    logout();

    // Show appropriate message
    const errorMessage = getFriendlyErrorMessage(error);
    message.error(errorMessage || "Session expired. Please log in again.");

    // Redirect to login page
    navigate("/login", { replace: true });
  };

  /**
   * Handle authorization errors by showing access denied message
   */
  const handleAuthorizationError = (error: any) => {
    const errorMessage = getFriendlyErrorMessage(error);
    message.error(
      errorMessage ||
        "Access denied. You don't have permission for this action."
    );
  };

  /**
   * Show an error message with user-friendly text
   */
  const showError = (error: any, fallbackMessage?: string) => {
    // Check if it's an authentication error first
    if (isAuthenticationError(error)) {
      console.log(
        "🔍 useErrorHandler: Authentication error detected, calling handleAuthenticationError"
      );
      handleAuthenticationError(error);
      return;
    }

    const errorMessage = getFriendlyErrorMessage(error);
    message.error(errorMessage || fallbackMessage || "An error occurred");
  };

  /**
   * Show a success message
   */
  const showSuccess = (messageText: string) => {
    message.success(messageText);
  };

  /**
   * Show a warning message
   */
  const showWarning = (messageText: string) => {
    message.warning(messageText);
  };

  /**
   * Show an info message
   */
  const showInfo = (messageText: string) => {
    message.info(messageText);
  };

  /**
   * Handle API errors with automatic authentication error detection
   */
  const handleApiError = (error: any, context?: string) => {
    // Check if it's an authentication error first
    if (isAuthenticationError(error)) {
      console.log(
        "🔍 useErrorHandler: Authentication error detected in handleApiError, calling handleAuthenticationError"
      );
      handleAuthenticationError(error);
      return;
    }

    const errorMessage = getFriendlyErrorMessage(error);

    // Add context if provided
    const fullMessage = context ? `${context}: ${errorMessage}` : errorMessage;

    message.error(fullMessage);
  };

  /**
   * Handle validation errors specifically
   */
  const handleValidationError = (error: any) => {
    const errorMessage = getFriendlyErrorMessage(error);
    message.error(errorMessage);
  };

  /**
   * Handle authentication errors (legacy method, now handled automatically)
   */
  const handleAuthError = (error: any) => {
    // This is now handled automatically in showError and handleApiError
    // But we keep it for backward compatibility
    if (isAuthenticationError(error)) {
      handleAuthenticationError(error);
    } else {
      const errorMessage = getFriendlyErrorMessage(error);
      message.error(errorMessage);
    }
  };

  return {
    showError,
    showSuccess,
    showWarning,
    showInfo,
    handleApiError,
    handleValidationError,
    handleAuthError,
    isAuthenticationError,
    isAuthorizationError,
    handleAuthenticationError,
    handleAuthorizationError,
  };
}
